import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge, Flame, Minus, Plus, Save, Trophy } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const FIELDS = ["outreach_dms", "links_shared", "posts_audited", "active_chats", "scopes_drafted"];
const cardCls = "border border-white/10 bg-black/40";

export default function SalesKpi() {
  const [data, setData] = useState(null);
  const [today, setToday] = useState({});
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(() => {
    api.get("/sales/kpi/my?days=14").then((r) => {
      setData(r.data);
      const log = (r.data.logs || []).find((l) => l.date === r.data.today) || {};
      const vals = {};
      FIELDS.forEach((f) => { vals[f] = log[f] || 0; });
      setToday(vals);
      setDirty(false);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const bump = (f, delta) => {
    setToday((t) => ({ ...t, [f]: Math.max(0, (t[f] || 0) + delta) }));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/sales/kpi/log", today);
      toast.success("Activity logged — keep the streak alive");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const targets = data.targets || {};
  const goldHits = FIELDS.filter((f) => (today[f] || 0) >= (targets[f]?.gold ?? Infinity)).length;
  const history = [...(data.logs || [])].reverse();

  return (
    <div data-testid="sales-kpi-card" className={cardCls}>
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
          <Gauge className="w-4 h-4 text-nc-orange" /> Daily KPI Tracker
        </h2>
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase">
          <span data-testid="kpi-streak" className={`flex items-center gap-1.5 ${data.streak > 0 ? "text-nc-orange" : "text-zinc-500"}`}>
            <Flame className="w-3.5 h-3.5" /> {data.streak} day streak
          </span>
          <span data-testid="kpi-gold-hits" className={`flex items-center gap-1.5 ${goldHits > 0 ? "text-nc-green" : "text-zinc-500"}`}>
            <Trophy className="w-3.5 h-3.5" /> {goldHits}/5 gold today
          </span>
        </div>
      </div>
      <div className="p-5 grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-5">
        <div className="flex flex-col gap-2.5">
          {FIELDS.map((f) => {
            const t = targets[f] || {};
            const val = today[f] || 0;
            const pctGold = t.gold ? Math.min(100, Math.round((val / t.gold) * 100)) : 0;
            const isGold = t.gold != null && val >= t.gold;
            return (
              <div key={f} data-testid={`kpi-row-${f}`} className="grid grid-cols-[1fr_auto] items-center gap-3 border border-white/5 bg-[#050505]/60 px-3 py-2">
                <div className="min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] text-zinc-300 truncate">{t.label || f}</span>
                    <span className="font-mono text-[9px] text-zinc-600 shrink-0">std {t.standard} // gold <span className="text-nc-orange">{t.gold}</span></span>
                  </div>
                  <div className="h-1 bg-white/5 mt-1.5">
                    <div className={`h-1 transition-all duration-500 ${isGold ? "bg-nc-orange" : "bg-nc-blue"}`} style={{ width: `${pctGold}%` }} />
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button data-testid={`kpi-${f}-minus`} onClick={() => bump(f, -1)} className="w-6 h-6 border border-white/10 text-zinc-400 hover:text-white flex items-center justify-center transition-colors"><Minus className="w-3 h-3" /></button>
                  <span data-testid={`kpi-${f}-value`} className={`font-mono text-sm w-8 text-center ${isGold ? "text-nc-orange font-bold" : "text-zinc-100"}`}>{val}</span>
                  <button data-testid={`kpi-${f}-plus`} onClick={() => bump(f, 1)} className="w-6 h-6 border border-white/10 text-zinc-400 hover:text-white flex items-center justify-center transition-colors"><Plus className="w-3 h-3" /></button>
                </div>
              </div>
            );
          })}
          <button data-testid="kpi-save-button" disabled={busy || !dirty} onClick={save} className="self-start mt-1 bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 flex items-center gap-2 transition-colors">
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Log today's activity"}
          </button>
        </div>
        <div className="flex flex-col gap-2">
          <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase">Last 14 days — outreach</p>
          <div data-testid="kpi-history" className="flex items-end gap-1 h-28 border border-white/5 bg-[#050505]/60 p-2">
            {history.length === 0 && <p className="font-mono text-[9px] text-zinc-600 m-auto">No activity yet</p>}
            {history.map((l) => {
              const h = Math.max(4, Math.min(100, Math.round(((l.outreach_dms || 0) / 50) * 100)));
              return <div key={l.date} title={`${l.date}: ${l.outreach_dms} DMs`} className={`flex-1 ${(l.outreach_dms || 0) >= 50 ? "bg-nc-orange" : (l.outreach_dms || 0) >= 20 ? "bg-nc-blue" : "bg-white/15"}`} style={{ height: `${h}%` }} />;
            })}
          </div>
          <p className="font-mono text-[8px] text-zinc-600 leading-relaxed">Bars: daily outbound DMs. Orange = gold tier (50+), blue = standard (20+).</p>
        </div>
      </div>
    </div>
  );
}
